import CryptoJS from 'crypto-js';

/**
 * JWT Helper Utility
 * Generates and manages JWT tokens for API authentication using crypto-js (Synchronous)
 */

const JWT_SECRET = process.env.VUE_APP_JWT_SECRET;
const JWT_ISSUER = 'axes2-api';
const JWT_AUDIENCE = 'axes2-app';
const TOKEN_EXPIRATION_HOURS = 24;

// Cache for the current token
let cachedToken = null;
let tokenExpiration = null;

// Base64URL encoding helper
function base64UrlEncode(str) {
    return str.replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

function signJwt(payload, secret) {
    const header = {
        alg: "HS256",
        typ: "JWT"
    };

    const now = Math.floor(Date.now() / 1000);
    const fullPayload = {
        ...payload,
        iat: now,
        exp: now + (TOKEN_EXPIRATION_HOURS * 3600),
        iss: JWT_ISSUER,
        aud: JWT_AUDIENCE
    };

    const stringifiedHeader = CryptoJS.enc.Utf8.parse(JSON.stringify(header));
    const encodedHeader = base64UrlEncode(CryptoJS.enc.Base64.stringify(stringifiedHeader));

    const stringifiedPayload = CryptoJS.enc.Utf8.parse(JSON.stringify(fullPayload));
    const encodedPayload = base64UrlEncode(CryptoJS.enc.Base64.stringify(stringifiedPayload));

    const data = encodedHeader + "." + encodedPayload;

    const signature = CryptoJS.HmacSHA256(data, secret);
    const encodedSignature = base64UrlEncode(CryptoJS.enc.Base64.stringify(signature));

    return data + "." + encodedSignature;
}

/**
 * Generate a new JWT token
 * @param {string} user - Username or identifier
 * @param {string} role - User role (e.g., 'axes2')
 * @returns {string} JWT token
 */
export function generateToken(user = 'axes2', role = 'axes2') {
    const payload = {
        user,
        role
    };

    try {
        const token = signJwt(payload, JWT_SECRET);

        // Cache the token
        cachedToken = token;
        // Set expiration
        tokenExpiration = Date.now() + ((TOKEN_EXPIRATION_HOURS - 1) * 60 * 60 * 1000); // 1 hour buffer

        return token;
    } catch (error) {
        console.error("Error generating token:", error);
        return null;
    }
}

/**
 * Get a valid JWT token (generates new one if expired or missing)
 * @returns {string} Valid JWT token
 */
export function getToken() {
    // Check if we have a cached token that hasn't expired
    if (cachedToken && tokenExpiration && Date.now() < tokenExpiration) {
        return cachedToken;
    }

    // Generate a new token
    return generateToken();
}

/**
 * Get the token formatted for HTTP Authorization header
 * @returns {string} Bearer token
 */
export function getBearerToken() {
    return `Bearer ${getToken()}`;
}

/**
 * Clear the cached token (useful for logout or testing)
 */
export function clearToken() {
    cachedToken = null;
    tokenExpiration = null;
}

export default {
    generateToken,
    getToken,
    getBearerToken,
    clearToken
};
